import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetIdentity, useNotify, useTranslate } from 'react-admin';
import {
  BbLbPayoutEntry,
  BbLbPhase,
  BbLbPhaseBoard,
  BbLbPhaseSettlement,
  bbLbBanUser,
  bbLbBulkPay,
  bbLbGetPhaseBoards,
  bbLbGetSettlement,
  bbLbPayEntry,
  bbLbApproveReplace,
  bbLbCancelReplace,
  bbLbListReplaceRequests,
  bbLbRejectReplace,
  bbLbRequestReplace,
  bbLbRetryEntry,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { RequestsTable } from '../../money-requests/RequestsTable';
import { BanUserDialog, UnbanConfirmDialog } from '../banned-users/BanDialogs';
import { useCurrentUserId, useRequestList } from '../../money-requests/useMoneyRequests';
import {
  METRIC_I18N,
  PAYOUT_STATUS_COLOR,
  REWARD_KIND_I18N,
  STATUS_META,
  boardLabel,
  decimalOnly,
  fmtReward,
  fmtNum,
  fmtTime,
} from './helpers';

// ── Replace-entry dialog: creates a REQUEST (wallet / amount), a different admin approves it ──────

function EntryEditDialog({
  entry,
  onClose,
  onSaved,
}: {
  entry: BbLbPayoutEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [wallet, setWallet] = useState(entry.walletAddress ?? '');
  const [amount, setAmount] = useState(String(entry.rewardAmount ?? ''));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Send only what changed: the approval re-distributes the entry with these values.
  const newWallet = wallet.trim() !== (entry.walletAddress ?? '') ? wallet.trim() : '';
  const newAmount =
    String(amount).trim() !== String(entry.rewardAmount ?? '') ? String(amount).trim() : '';
  const hasChange = !!newWallet || !!newAmount;

  const save = async () => {
    setSaving(true);
    try {
      await bbLbRequestReplace(entry.id, {
        walletAddress: newWallet || undefined,
        rewardAmount: newAmount || undefined,
        reason: reason.trim() || undefined,
      });
      notify(
        t('module.blindBox.lbReplaceRequested', {
          _: 'Replace requested. It takes effect only when a different admin approves it.',
        }),
        { type: 'success' }
      );
      onSaved();
      onClose();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('module.blindBox.lbEditEntry')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning" variant="outlined">
            {t('module.blindBox.lbReplaceWarning', {
              _: 'This creates a request. When a different admin approves it, the entry is re-distributed and paid to the wallet below.',
            })}
          </Alert>
          <TextField
            size="small"
            label={t('module.blindBox.mdWalletAddress')}
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={`${t('module.blindBox.lbColReward')} (${t(REWARD_KIND_I18N[entry.rewardKind])})`}
            value={amount}
            onChange={(e) => setAmount(decimalOnly(e.target.value))}
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('module.moneyRequests.reason', { _: 'Reason' })}
            value={reason}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <PermissionButton resource="bb-leaderboard-payout" action="edit">
          <Button variant="contained" onClick={save} disabled={saving || !hasChange}>
            {t('module.blindBox.lbRequestReplace', { _: 'Request replace' })}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Pending replace requests: approve / reject / cancel ───────────────────────

function ReplaceRequestsPanel({ reloadKey, onDecided }: { reloadKey: number; onDecided: () => void }) {
  const t = useTranslate();
  const currentUserId = useCurrentUserId();
  const requests = useRequestList(bbLbListReplaceRequests);
  const { load } = requests;

  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    load();
  }, [reloadKey, load]);

  return (
    <PermissionGuard
      anyOf={[
        { resource: 'bb-leaderboard-payout', action: 'edit' },
        { resource: 'bb-leaderboard-payout', action: 'approve' },
      ]}
      fallback={<></>}
      loadingFallback={<></>}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">
            {t('module.blindBox.lbReplaceRequests', { _: 'Pending replace requests' })}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={load}>
            {t('module.moneyRequests.reload', { _: 'Reload' })}
          </Button>
        </Stack>
        <RequestsTable
          rows={requests.rows}
          loading={requests.loading}
          currentUserId={currentUserId}
          busyId={requests.busyId}
          approvePermission={{ resource: 'bb-leaderboard-payout', action: 'approve' }}
          cancelPermission={{ resource: 'bb-leaderboard-payout', action: 'edit' }}
          summary={(r) => (
            <span>
              entry #{r.payload.entryId}
              {r.payload.walletAddress ? ` | wallet: ${r.payload.walletAddress}` : ''}
              {r.payload.rewardAmount ? ` | amount: ${r.payload.rewardAmount}` : ''}
            </span>
          )}
          onApprove={async (r) => {
            const to = r.payload.walletAddress || t('module.blindBox.lbCurrentWallet', { _: "the entry's current wallet" });
            if (
              !window.confirm(
                t('module.blindBox.lbApproveReplaceConfirm', {
                  _: 'Approving re-distributes this entry: it will be paid to %{wallet}. Continue?',
                  wallet: to,
                })
              )
            ) {
              return;
            }
            await requests.run(r.id, () => bbLbApproveReplace(r.id), t('module.moneyRequests.approved', { _: 'Approved' }));
            onDecided();
          }}
          onReject={(r, reason) =>
            requests.run(r.id, () => bbLbRejectReplace(r.id, reason), t('module.moneyRequests.rejected', { _: 'Rejected' }))
          }
          onCancel={(r) =>
            requests.run(r.id, () => bbLbCancelReplace(r.id), t('module.moneyRequests.cancelled', { _: 'Cancelled' }))
          }
        />
      </Stack>
    </PermissionGuard>
  );
}

// ── Per-winner ban / unban toggle (banned winners' payouts are held at settlement) ──

function BanUserButton({
  userId,
  walletAddress,
  isBanned,
  disabled,
  onDone,
}: {
  userId?: string | null;
  walletAddress?: string | null;
  isBanned: boolean;
  disabled?: boolean;
  onDone: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const { data: identity } = useGetIdentity();
  const [dialog, setDialog] = useState<'ban' | 'unban' | null>(null);
  // The server records the operator from the JWT; we still send the display name (harmless).
  const operator =
    (identity as any)?.username || (identity as any)?.name || (identity as any)?.email || '';

  const submit = async (
    body: { userId?: string; walletAddress?: string; reason?: string },
    banned: boolean
  ) => {
    try {
      await bbLbBanUser({ ...body, banned, operator: operator.trim() || undefined });
      notify(t(banned ? 'module.blindBox.lbBanned' : 'module.blindBox.lbUnbanned'), {
        type: 'success',
      });
      setDialog(null);
      onDone();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      throw e;
    }
  };

  // Unban is always allowed; only the ban direction is gated.
  const blocked = !!disabled && !isBanned;
  const title = blocked
    ? t('module.blindBox.lbBanDisabledHint')
    : t(isBanned ? 'module.blindBox.lbUnban' : 'module.blindBox.lbBan');

  return (
    <PermissionButton resource="bb-leaderboard" action="update">
      <Tooltip title={title} arrow>
        <span>
          <IconButton
            size="small"
            disabled={blocked}
            onClick={() => setDialog(isBanned ? 'unban' : 'ban')}
          >
            <BlockIcon fontSize="small" color={isBanned ? 'error' : 'inherit'} />
          </IconButton>
        </span>
      </Tooltip>
      <BanUserDialog
        open={dialog === 'ban'}
        target={{ userId, walletAddress }}
        onClose={() => setDialog(null)}
        onConfirm={(body) =>
          submit(
            { userId: body.userId, walletAddress: body.walletAddress, reason: body.reason },
            true
          )
        }
      />
      <UnbanConfirmDialog
        open={dialog === 'unban'}
        userLabel={userId || walletAddress || ''}
        onClose={() => setDialog(null)}
        onConfirm={() => submit({ userId: userId || undefined }, false)}
      />
    </PermissionButton>
  );
}

// ── Settlement tab ────────────────────────────────────────────────────────────

export function SettlementTab({ phases }: { phases: BbLbPhase[] }) {
  const t = useTranslate();
  const notify = useNotify();
  const [phaseId, setPhaseId] = useState('');
  const [boards, setBoards] = useState<BbLbPhaseBoard[]>([]);
  const [boardId, setBoardId] = useState('');
  const [data, setData] = useState<BbLbPhaseSettlement | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editEntry, setEditEntry] = useState<BbLbPayoutEntry | null>(null);
  const [replaceReloadKey, setReplaceReloadKey] = useState(0);

  // Settlement only applies to ended/frozen phases, so default off any upcoming phase.
  useEffect(() => {
    if (phaseId || !phases.length) return;
    const target =
      phases.find((p) => p.status === 'active') ??
      phases.find((p) => p.status === 'completed') ??
      phases.find((p) => p.status !== 'upcoming') ??
      phases[0];
    setPhaseId(target.id);
  }, [phases, phaseId]);

  // Load the selected phase's boards; settlement is keyed by a board (phaseBoardId).
  useEffect(() => {
    if (!phaseId) {
      setBoards([]);
      return;
    }
    bbLbGetPhaseBoards(phaseId)
      .then((rows) =>
        setBoards([...rows].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)))
      )
      .catch(() => setBoards([]));
  }, [phaseId]);

  // Keep the board selection pointing at a board of the current phase.
  useEffect(() => {
    if (boards.some((b) => b.id === boardId)) return;
    setBoardId(boards[0]?.id ?? '');
  }, [boards, boardId]);

  const selectedPhase = useMemo(() => phases.find((p) => p.id === phaseId), [phases, phaseId]);
  const selectedBoard = useMemo(() => boards.find((b) => b.id === boardId), [boards, boardId]);
  // The value column shows the selected board's metric — Volume or PnL.
  const valueColLabel = selectedBoard
    ? t(METRIC_I18N[selectedBoard.metric])
    : t('module.blindBox.lbColValue');
  // Payout is only meaningful once the phase has ended (its ledger is frozen).
  const isEnded = selectedPhase?.status === 'completed';
  // A live preview (isFrozen === false): entries are projected, nothing is distributable — hide the
  // Distribute controls. Absent/true → real frozen ledger.
  const isPreview = data?.isFrozen === false;
  // Banning is allowed only while it can still hold a payout: an active phase, or an ended phase whose
  // board still has undistributed (non-completed) entries. Once everything is paid, there's nothing to hold.
  const allDistributed =
    !!data && data.entries.length > 0 && data.entries.every((e) => e.status === 'completed');
  const canBan =
    selectedPhase?.status === 'active' ||
    (selectedPhase?.status === 'completed' && !allDistributed);

  const load = useCallback(() => {
    if (!boardId) {
      setData(null);
      return;
    }
    setLoading(true);
    bbLbGetSettlement(boardId)
      .then(setData)
      .catch((e: any) => {
        notify(e?.message || 'Error', { type: 'error' });
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [boardId, notify]);

  console.log('SettlementTab', data);

  useEffect(() => {
    load();
  }, [load]);

  const runEntry = async (entry: BbLbPayoutEntry, fn: (id: string) => Promise<BbLbPayoutEntry>) => {
    setBusyId(entry.id);
    try {
      await fn(entry.id);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const bulkPay = async () => {
    if (!isEnded) {
      notify(t('module.blindBox.lbBulkPayEndedOnly'), { type: 'warning' });
      return;
    }
    if (!window.confirm(t('module.blindBox.lbBulkPayConfirm'))) return;
    setBulkBusy(true);
    try {
      const res = await bbLbBulkPay(boardId);
      notify(t('module.blindBox.lbBulkPayDone', { paid: res.paid, failed: res.failed }), {
        type: res.failed ? 'warning' : 'success',
      });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setBulkBusy(false);
    }
  };

  // A winner earning both currencies is two entries — group them so Rank/User/Value can span the rows.
  const groupedEntries = useMemo(() => {
    const map = new Map<string, BbLbPayoutEntry[]>();
    const order: string[] = [];
    (data?.entries ?? []).forEach((en) => {
      const key = `${en.rank}|${en.userId}`;
      const arr = map.get(key);
      if (arr) arr.push(en);
      else {
        map.set(key, [en]);
        order.push(key);
      }
    });
    return order.map((k) => map.get(k) as BbLbPayoutEntry[]);
  }, [data]);

  const tiles = useMemo(
    () => [
      { label: t('module.blindBox.lbOnList'), value: data ? String(data.onListCount) : '—' },
      {
        label: t('module.blindBox.lbStatDistributed'),
        value: data ? String(data.distributedCount) : '—',
      },
      {
        label: t('module.blindBox.lbUsdcSent'),
        value: data ? `${fmtNum(data.sentUsdc)} / ${fmtNum(data.totalUsdcToSend)}` : '—',
      },
      {
        label: t('module.blindBox.lbBoxSent'),
        value: data ? `${fmtNum(data.sentBox)} / ${fmtNum(data.totalBoxToSend)}` : '—',
      },
    ],
    [data, t]
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('module.blindBox.lbPhase')}
          value={phaseId}
          onChange={(e) => setPhaseId(e.target.value)}
          sx={{ minWidth: 240, height: 40, '& .MuiInputBase-root': { minHeight: 40 } }}
        >
          {phases.map((p) => (
            <MenuItem key={p.id} value={p.id} disabled={p.status === 'upcoming'}>
              {p.name || p.id} · {t(STATUS_META[p.status]?.key ?? 'module.blindBox.lbStatus')}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('module.blindBox.lbBoard')}
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
          disabled={!boards.length}
          sx={{ minWidth: 240, height: 40, '& .MuiInputBase-root': { minHeight: 40 } }}
        >
          {boards.length === 0 ? (
            <MenuItem value="" disabled>
              {t('module.blindBox.lbNoBoards')}
            </MenuItem>
          ) : (
            boards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {boardLabel(b, t)}
              </MenuItem>
            ))
          )}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {!isPreview ? (
          <Tooltip title={isEnded ? '' : t('module.blindBox.lbBulkPayEndedOnly')} arrow>
            <span>
              <PermissionButton resource="bb-leaderboard-payout" action="pay">
                <Button
                  variant="contained"
                  disabled={
                    bulkBusy ||
                    !data ||
                    !isEnded ||
                    data.onListCount === 0 ||
                    data.distributedCount === data.onListCount
                  }
                  onClick={bulkPay}
                  sx={{ height: 40, px: 1.5 }}
                >
                  {t('module.blindBox.lbBulkPay')}
                </Button>
              </PermissionButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {isPreview ? (
        <Alert severity="info" variant="outlined">
          {t('module.blindBox.lbPreviewHint')}
        </Alert>
      ) : data && data.payoutEligibleAt ? (
        <Alert severity="info" variant="outlined">
          {t('module.blindBox.lbPayoutEligibleAt', { time: fmtTime(data.payoutEligibleAt) })}
        </Alert>
      ) : data && data.isFrozen && data.payoutEligibleAt == null ? (
        <Alert severity="warning" variant="outlined">
          {t('module.blindBox.lbPayoutPaused')}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={6} md={3} key={tile.label}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {tile.label}
                </Typography>
                <Typography variant="h6">{tile.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80, maxWidth: 80, verticalAlign: 'middle', textAlign: 'center' }}>
                {t('module.blindBox.lbColRank')}
              </TableCell>
              <TableCell>{t('module.blindBox.lbColUser')}</TableCell>
              <TableCell align="right">{valueColLabel}</TableCell>
              <TableCell align="right">{t('module.blindBox.lbColReward')}</TableCell>
              <TableCell>{t('module.blindBox.lbColPayStatus')}</TableCell>
              <TableCell>{t('module.blindBox.lbColDistributedAt')}</TableCell>
              <TableCell align="right">{t('module.blindBox.mdColOperate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.loading', { _: 'Loading...' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : !data || data.entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.blindBox.lbNoSettlement')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              groupedEntries.map((group) =>
                group.map((en, idx) => {
                  const isCompleted = en.status === 'completed';
                  const isFailed = en.status === 'failed';
                  const busy = busyId === en.id;
                  const first = idx === 0;
                  return (
                    <TableRow
                      key={en.id}
                      sx={{ bgcolor: en.rank % 2 === 0 ? 'action.hover' : 'transparent' }}
                    >
                      {first ? (
                        <>
                          <TableCell
                            rowSpan={group.length}
                            sx={{ verticalAlign: 'middle', textAlign: 'center' }}
                          >
                            #{en.rank}
                          </TableCell>
                          <TableCell rowSpan={group.length} sx={{ verticalAlign: 'middle' }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Tooltip title={en.walletAddress || en.userId || ''} arrow>
                                <span style={{ wordBreak: 'break-all' }}>{en.userId}</span>
                              </Tooltip>
                              {en.isBanned ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  label={t('module.blindBox.lbBannedTag')}
                                />
                              ) : null}
                              <BanUserButton
                                userId={en.userId}
                                walletAddress={en.walletAddress}
                                isBanned={!!en.isBanned}
                                disabled={!canBan}
                                onDone={load}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell
                            rowSpan={group.length}
                            align="right"
                            sx={{ verticalAlign: 'middle' }}
                          >
                            ${fmtNum(en.value)}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        {fmtReward(en.rewardAmount, en.rewardKind, t)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={en.failReason || ''} arrow>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={PAYOUT_STATUS_COLOR[en.status] ?? 'default'}
                            label={en.status}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(en.distributedAt)}
                      </TableCell>
                      <TableCell align="right">
                        {isPreview ? null : en.isBanned && !isCompleted ? (
                          <Tooltip title={t('module.blindBox.lbBannedPayHint')} arrow>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('module.blindBox.lbBannedHeld')}
                            />
                          </Tooltip>
                        ) : (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {isFailed ? (
                              <PermissionButton resource="bb-leaderboard-payout" action="pay">
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => runEntry(en, bbLbRetryEntry)}
                                  variant="contained"
                                  color="warning"
                                >
                                  {t('module.blindBox.lbRetry')}
                                </Button>
                              </PermissionButton>
                            ) : null}
                            {!isCompleted ? (
                              <PermissionButton resource="bb-leaderboard-payout" action="pay">
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => runEntry(en, bbLbPayEntry)}
                                  variant="contained"
                                >
                                  {t('module.blindBox.lbPay')}
                                </Button>
                              </PermissionButton>
                            ) : null}
                            {!isCompleted ? (
                              <PermissionButton resource="bb-leaderboard-payout" action="edit">
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => setEditEntry(en)}
                                  variant="outlined"
                                >
                                  {t('actions.edit')}
                                </Button>
                              </PermissionButton>
                            ) : null}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ReplaceRequestsPanel reloadKey={replaceReloadKey} onDecided={load} />

      {editEntry ? (
        <EntryEditDialog
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={() => setReplaceReloadKey((k) => k + 1)}
        />
      ) : null}
    </Stack>
  );
}
